// Core DJ Engine for Deck A and Deck B with Pitch, EQ, Bipolar Filter, Hot Cues, Looping, SFX & Vinyl Scratching

export class DJEngine {
  constructor() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AudioContextClass();
    
    // Master Output & Analyser
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.9;
    
    this.masterAnalyser = this.ctx.createAnalyser();
    this.masterAnalyser.fftSize = 256;
    
    this.masterGain.connect(this.masterAnalyser);
    this.masterAnalyser.connect(this.ctx.destination);

    // Crossfader & Decks
    this.deckA = this.createDeck('A');
    this.deckB = this.createDeck('B');

    this.crossfaderPosition = 0.5;
    this.updateCrossfader(0.5);

    // Recording Engine
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.isRecording = false;

    // SFX Gain Bus
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = 0.85;
    this.sfxGain.connect(this.masterGain);
  }

  ensureContext() {
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  createDeck(deckId) {
    const deck = {
      id: deckId,
      buffer: null,
      sourceNode: null,
      isPlaying: false,
      isPaused: true,
      playbackRate: 1.0,
      pitchPercent: 0,
      startTime: 0,
      pauseOffset: 0,
      cuePoint: 0,
      duration: 0,
      rotationAngle: 0,
      isScratching: false,
      scratchLastAngle: 0,
      scratchLastTime: 0,
      scratchVelocity: 0,
      isBraking: false,

      // Hot Cues (Up to 4 positions in seconds)
      hotCues: [null, null, null, null],

      // Looping Engine
      loopActive: false,
      loopStart: 0,
      loopEnd: 0,
      loopBeats: 4,

      // Audio Nodes
      gainNode: this.ctx.createGain(),
      crossfadeGain: this.ctx.createGain(),
      eqLow: this.ctx.createBiquadFilter(),
      eqMid: this.ctx.createBiquadFilter(),
      eqHigh: this.ctx.createBiquadFilter(),
      filterNode: this.ctx.createBiquadFilter(), // Bipolar HPF / LPF filter
      analyser: this.ctx.createAnalyser()
    };

    // 3-Band EQ Filters
    deck.eqLow.type = 'lowshelf';
    deck.eqLow.frequency.value = 280;
    deck.eqLow.gain.value = 0;

    deck.eqMid.type = 'peaking';
    deck.eqMid.frequency.value = 1200;
    deck.eqMid.Q.value = 1.0;
    deck.eqMid.gain.value = 0;

    deck.eqHigh.type = 'highshelf';
    deck.eqHigh.frequency.value = 3500;
    deck.eqHigh.gain.value = 0;

    // Dedicated Sweep Filter (Neutral at allpass/bypassed)
    deck.filterNode.type = 'allpass';
    deck.filterNode.frequency.value = 1000;
    deck.filterNode.Q.value = 1.2;

    deck.analyser.fftSize = 128;

    // Chain: EQ Low -> EQ Mid -> EQ High -> Sweep Filter -> Gain -> Analyser -> Crossfade -> Master
    deck.eqLow.connect(deck.eqMid);
    deck.eqMid.connect(deck.eqHigh);
    deck.eqHigh.connect(deck.filterNode);
    deck.filterNode.connect(deck.gainNode);
    deck.gainNode.connect(deck.analyser);
    deck.analyser.connect(deck.crossfadeGain);
    deck.crossfadeGain.connect(this.masterGain);

    return deck;
  }

  loadAudioBuffer(deckId, audioBuffer) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    this.stopDeck(deckId);
    deck.buffer = audioBuffer;
    deck.duration = audioBuffer.duration;
    deck.pauseOffset = 0;
    deck.cuePoint = 0;
    deck.hotCues = [null, null, null, null];
    deck.loopActive = false;
  }

  async loadAudioFile(deckId, file) {
    this.ensureContext();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
    this.loadAudioBuffer(deckId, audioBuffer);
    return audioBuffer;
  }

  playDeck(deckId) {
    this.ensureContext();
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.buffer || deck.isPlaying) return;

    deck.sourceNode = this.ctx.createBufferSource();
    deck.sourceNode.buffer = deck.buffer;
    deck.sourceNode.loop = !deck.loopActive; // If loop active, custom loop range
    if (deck.loopActive) {
      deck.sourceNode.loop = true;
      deck.sourceNode.loopStart = deck.loopStart;
      deck.sourceNode.loopEnd = deck.loopEnd;
    }
    deck.sourceNode.playbackRate.value = deck.playbackRate;

    deck.sourceNode.connect(deck.eqLow);

    const offset = deck.pauseOffset % deck.duration;
    deck.sourceNode.start(0, offset);
    deck.startTime = this.ctx.currentTime - (offset / deck.playbackRate);

    deck.isPlaying = true;
    deck.isPaused = false;
    deck.isBraking = false;
  }

  pauseDeck(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.isPlaying || !deck.sourceNode) return;

    deck.pauseOffset = (this.ctx.currentTime - deck.startTime) * deck.playbackRate;
    try {
      deck.sourceNode.stop();
      deck.sourceNode.disconnect();
    } catch (e) {}
    deck.sourceNode = null;
    deck.isPlaying = false;
    deck.isPaused = true;
  }

  stopDeck(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (deck.sourceNode) {
      try {
        deck.sourceNode.stop();
        deck.sourceNode.disconnect();
      } catch (e) {}
      deck.sourceNode = null;
    }
    deck.isPlaying = false;
    deck.isPaused = true;
    deck.pauseOffset = 0;
    deck.isBraking = false;
  }

  // Motor Brake Simulation (Realistic Turntable Stop)
  triggerBrake(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.isPlaying || !deck.sourceNode || deck.isBraking) return;

    deck.isBraking = true;
    const now = this.ctx.currentTime;
    // Exponential deceleration to near zero in 1.1 seconds
    deck.sourceNode.playbackRate.setValueAtTime(deck.playbackRate, now);
    deck.sourceNode.playbackRate.exponentialRampToValueAtTime(0.01, now + 1.1);

    setTimeout(() => {
      if (deck.isBraking) {
        this.pauseDeck(deckId);
        deck.isBraking = false;
      }
    }, 1150);
  }

  // Hot Cues
  triggerHotCue(deckId, cueIndex) {
    this.ensureContext();
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.buffer) return null;

    if (deck.hotCues[cueIndex] === null) {
      // Set cue point at current playhead position
      const pos = this.getCurrentPlayPosition(deckId);
      deck.hotCues[cueIndex] = pos.current;
      return { action: 'set', time: pos.current };
    } else {
      // Jump and trigger playback from hot cue
      const targetTime = deck.hotCues[cueIndex];
      this.stopDeck(deckId);
      deck.pauseOffset = targetTime;
      this.playDeck(deckId);
      return { action: 'jump', time: targetTime };
    }
  }

  clearHotCue(deckId, cueIndex) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    deck.hotCues[cueIndex] = null;
  }

  // Beat Looping
  toggleLoop(deckId, beats, bpm) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.buffer) return false;

    if (deck.loopActive && deck.loopBeats === beats) {
      // Turn off loop
      deck.loopActive = false;
      if (deck.sourceNode) {
        deck.sourceNode.loop = true;
        deck.sourceNode.loopStart = 0;
        deck.sourceNode.loopEnd = deck.duration;
      }
      return false;
    }

    // Activate loop with length calculated from current playhead
    const pos = this.getCurrentPlayPosition(deckId);
    const loopDuration = (60 / bpm) * beats;
    deck.loopStart = pos.current;
    deck.loopEnd = Math.min(deck.duration, pos.current + loopDuration);
    deck.loopBeats = beats;
    deck.loopActive = true;

    if (deck.sourceNode) {
      deck.sourceNode.loop = true;
      deck.sourceNode.loopStart = deck.loopStart;
      deck.sourceNode.loopEnd = deck.loopEnd;
    }
    return true;
  }

  // Bipolar Sweep Filter (-100 to +100)
  // -100 to -1 = Low Pass Filter (20000Hz down to 200Hz)
  // 0 = Neutral / Bypassed
  // +1 to +100 = High Pass Filter (20Hz up to 5000Hz)
  setSweepFilter(deckId, value) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const now = this.ctx.currentTime;

    if (Math.abs(value) < 3) {
      deck.filterNode.type = 'allpass';
      deck.filterNode.frequency.setTargetAtTime(1000, now, 0.02);
    } else if (value < 0) {
      // Low pass
      deck.filterNode.type = 'lowpass';
      const norm = Math.abs(value) / 100;
      const freq = 20000 * Math.pow(0.01, norm); // 20kHz down to 200Hz
      deck.filterNode.frequency.setTargetAtTime(Math.max(150, freq), now, 0.02);
    } else {
      // High pass
      deck.filterNode.type = 'highpass';
      const norm = value / 100;
      const freq = 30 + 4800 * Math.pow(norm, 2); // 30Hz up to ~5kHz
      deck.filterNode.frequency.setTargetAtTime(freq, now, 0.02);
    }
  }

  setCue(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (deck.isPlaying) {
      this.pauseDeck(deckId);
      deck.cuePoint = deck.pauseOffset;
    } else {
      deck.pauseOffset = deck.cuePoint;
    }
  }

  jumpToCue(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const wasPlaying = deck.isPlaying;
    this.stopDeck(deckId);
    deck.pauseOffset = deck.cuePoint;
    if (wasPlaying) {
      this.playDeck(deckId);
    }
  }

  setPitch(deckId, pitchPercent) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    deck.pitchPercent = pitchPercent;
    const rate = 1.0 + (pitchPercent / 100);
    deck.playbackRate = Math.max(0.1, Math.min(3.0, rate));

    if (deck.sourceNode && deck.isPlaying && !deck.isBraking) {
      deck.sourceNode.playbackRate.setValueAtTime(deck.playbackRate, this.ctx.currentTime);
    }
  }

  setEQ(deckId, band, gainDb) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const clampedGain = Math.max(-48, Math.min(12, gainDb));
    if (band === 'low') {
      deck.eqLow.gain.setTargetAtTime(clampedGain, this.ctx.currentTime, 0.02);
    } else if (band === 'mid') {
      deck.eqMid.gain.setTargetAtTime(clampedGain, this.ctx.currentTime, 0.02);
    } else if (band === 'high') {
      deck.eqHigh.gain.setTargetAtTime(clampedGain, this.ctx.currentTime, 0.02);
    }
  }

  setVolume(deckId, gainValue) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    deck.gainNode.gain.setTargetAtTime(gainValue, this.ctx.currentTime, 0.02);
  }

  updateCrossfader(val) {
    this.crossfaderPosition = val;
    const gainA = Math.cos(val * 0.5 * Math.PI);
    const gainB = Math.cos((1.0 - val) * 0.5 * Math.PI);

    this.deckA.crossfadeGain.gain.setTargetAtTime(gainA, this.ctx.currentTime, 0.01);
    this.deckB.crossfadeGain.gain.setTargetAtTime(gainB, this.ctx.currentTime, 0.01);
  }

  getCurrentPlayPosition(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.buffer || deck.duration === 0) return { current: 0, progress: 0, duration: 0 };

    let current = 0;
    if (deck.isPlaying) {
      current = ((this.ctx.currentTime - deck.startTime) * deck.playbackRate) % deck.duration;
    } else {
      current = deck.pauseOffset % deck.duration;
    }
    const progress = Math.max(0, Math.min(1, current / deck.duration));
    return { current, progress, duration: deck.duration };
  }

  // Scratching Handler
  startScratch(deckId, initialAngle) {
    this.ensureContext();
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    deck.isScratching = true;
    deck.scratchLastAngle = initialAngle;
    deck.scratchLastTime = performance.now();
    deck.scratchVelocity = 0;

    if (!deck.isPlaying && deck.buffer) {
      this.playDeck(deckId);
    }
  }

  updateScratch(deckId, currentAngle) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.isScratching || !deck.sourceNode) return;

    const now = performance.now();
    const dt = Math.max(1, now - deck.scratchLastTime) / 1000;
    
    let dAngle = currentAngle - deck.scratchLastAngle;
    if (dAngle > 180) dAngle -= 360;
    if (dAngle < -180) dAngle += 360;

    const angularSpeed = (dAngle * (Math.PI / 180)) / dt;
    let targetRate = angularSpeed / 3.5;
    targetRate = Math.max(-2.5, Math.min(2.5, targetRate));

    deck.sourceNode.playbackRate.setValueAtTime(targetRate, this.ctx.currentTime);

    deck.scratchLastAngle = currentAngle;
    deck.scratchLastTime = now;
  }

  endScratch(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck.isScratching) return;
    deck.isScratching = false;

    if (deck.sourceNode && deck.isPlaying && !deck.isBraking) {
      deck.sourceNode.playbackRate.setValueAtTime(deck.playbackRate, this.ctx.currentTime);
    }
  }

  // Play One-Shot Sample SFX
  playOneShotSFX(audioBuffer) {
    this.ensureContext();
    if (!audioBuffer) return;
    const src = this.ctx.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(this.sfxGain);
    src.start();
  }

  // Master Mix Recording
  startRecording() {
    this.ensureContext();
    if (this.isRecording) return;

    const dest = this.ctx.createMediaStreamDestination();
    this.masterGain.connect(dest);

    this.recordedChunks = [];
    this.mediaRecorder = new MediaRecorder(dest.stream);

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.recordedChunks.push(e.data);
      }
    };

    this.mediaRecorder.start();
    this.isRecording = true;
  }

  stopRecording() {
    return new Promise((resolve) => {
      if (!this.isRecording || !this.mediaRecorder) {
        resolve(null);
        return;
      }

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
        this.isRecording = false;
        resolve(blob);
      };

      this.mediaRecorder.stop();
    });
  }
}
